import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchJobsData,
  getJobsData,
  getJobsListWithBerthData,
} from "../../apis/voyages";
import { getBerthData } from "../../utils/berth-utils";

import type { Call, Voyage } from "../../utils/types/voyages";

// -------------------------------------- Helper Functions-----------------------------------------

export const transformJobsData = (jobs: any[]) => {
  try {
    if (!Array.isArray(jobs)) return [];

    const voyages = jobs.map((job: any) => {
      const berthRotation = Array.isArray(job.berth_rotations)
        ? job.berth_rotations
        : [];

      const berthData = job.berth_rotations ? getBerthData(job) : null;
      const portsDetails = Array.isArray(job.berths)
        ? job.berths.map((berth: any) => ({
            calls: Array.isArray(berth?.calls)
              ? berth.calls.map((call: Call) => ({
                  anchored_hours: call?.anchored_hours || 0,
                  avg_load_rate: call?.avg_load_rate || "",
                  berth_id: call?.berth_id || "",
                  bunkers: call?.bunkers || null,
                  bunkers_allowed: call?.bunkers_allowed || null,
                  deliveries: call?.deliveries || null,
                  eta: call?.eta || "",
                  etd: call?.etd || "",
                  freshwater_available: call?.freshwater_available || null,
                  gangway: call?.gangway || null,
                  heading: call?.heading || null,
                  hose_size: call?.hose_size || null,
                  max_depth_channel: call?.max_depth_channel || null,
                  max_draft_berth: call?.max_draft_berth || null,
                  name: call?.name || null,
                  notes: call?.notes || null,
                  on_board_services: call?.on_board_services || null,
                  operations: call?.operations || null,
                  portcall_id: call?.portcall_id || null,
                  portcall_name: call?.portcall_name || null,
                  position: call?.position || null,
                  shifting_hours: call?.shifting_hours || 0,
                  status: call?.status || null,
                  stores_allowed: call?.stores_allowed || null,
                  type: call?.type || null,
                  turn_over_notes: call?.turn_over_notes || null,
                  prospect_notes: call?.prospect_notes || null,
                  port_calls: call?.port_calls || null,
                  gate_id: call?.gate_id || null,
                  gates: call?.gates || null,
                }))
              : [],
            job_id: berth?.job_id || "",
            pc_id: berth?.pc_id || "",
            portcall_name: berth?.portcall_name || "",
            showBerthTable: berth?.showBerthTable || "",
          }))
        : [];

      return {
        id: job.job_id || "",
        name: job.vessel || "Unnamed Vessel",
        additionalText: "",
        status: job.status || "",
        persons: job.persons || [],
        ports:
          Array.isArray(job.ports) && job.ports[0] != null ? job.ports : null,
        agentName: job.assigned_agent || "",
        agentPhone: job.agent?.phone || null,
        agentPhoneExt: job.agent?.phone_ext || null,
        agentEmail: job.agent?.email || null,
        assigned_agent_id: job.assigned_agent_id || null,
        assigned_phone: job.assigned_phone || null,
        assigned_phone_name: job.assigned_phone_name || null,
        voyageNum: job.voyage || null,
        currentBerth: berthData?.currentBerth || null,
        nextBerth: berthData?.nextBerth || null,
        portsDetails,
        operator: job.operator || null,
        management: job.management || null,
        imo: job.imo || null,
        berthRotations: berthRotation,
        forAccountNotes: (() => {
          try {
            const notes: string[] = []; // Initialize notes as an array
            (job.berths || []).forEach((rotation: any) => {
              (rotation.calls || []).forEach((call: any) => {
                (call.deliveries || []).forEach((delivery: any) => {
                  const deliveryJobId =
                    delivery?.job_id ?? delivery?.jobId ?? null;
                  if (deliveryJobId == null) return;
                  if (String(deliveryJobId) !== String(job.job_id)) return;

                  const n =
                    delivery?.for_account_notes ||
                    delivery?.forAccountNotes ||
                    null;
                  if (n && String(n).trim()) notes.push(String(n).trim());
                });
              });
            });
            return notes.length ? notes.join(", ") : null; // Return concatenated notes or null
          } catch {
            return null;
          }
        })(),
        turnOverNotes: job.turn_over_notes || null,
        prospectNotes: job.prospect_notes || null,
        portCalls: job.port_calls || null,
        vessel_id: job.vessel_id || null,
        vessel: job.vessel || null,
        berths: job.berths || [],
        billing_reference: job.billing_reference || null,
        appointedManagement: job.appointed_management || null,
        appointedOperator: job.appointed_operator || null,
        companyFlag: job.company_flag || null,
        companyFlagComments: job.company_flag_comments || null,
        personFlag: job.agent?.person_flag || null,
        personFlagComments: job.agent?.person_flag_comments || null,
        status_id: job.status_id || null,
        company_id: job.company_id || null,
        mgmtCompanyAgentId: job.mgmt_company_agent_id || null,
        operatorContactId: job.operator_contact_id || null,
      };
    });

    return voyages as unknown as Voyage[];
  } catch {
    return [];
  }
};

// --------------------------------------------Thunks-------------------------------------------------

export const fetchVoyagesList = createAsyncThunk(
  "voyages/fetchVoyages",
  async (
    {
      page,
      pageSize,
      version,
      statusValue,
      q,
      sort,
      agentId,
    }: {
      page: number;
      pageSize: number | null;
      version: string;
      statusValue: string;
      q: string;
      sort?: {
        field: string;
        direction: string;
      };
      agentId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const jobs = await fetchJobsData(
        page,
        pageSize,
        version,
        statusValue,
        q,
        sort,
        agentId
      );
      const voyages = transformJobsData(jobs.data);
      return {
        voyages,
        total: jobs.total,
        version,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getVoyagesList = createAsyncThunk(
  "voyages/getVoyages",
  async ({ version }: { version?: string }, { rejectWithValue }) => {
    try {
      const jobs = await getJobsData(version);
      return {
        voyages: jobs.data,
        total: jobs.total,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch voyages");
    }
  }
);

export const fetchJobsListWithBerthData = createAsyncThunk(
  "voyages/fetchJobsListWithBerthData",
  async (
    {
      page,
      pageSize,
      version,
      statusValue,
      q,
      sort,
      agentId,
    }: {
      page: number;
      pageSize: number | null;
      version: string;
      statusValue: string;
      q: string;
      sort?: {
        field: string;
        direction: string;
      };
      agentId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const jobs = await getJobsListWithBerthData(
        page,
        pageSize,
        version,
        statusValue,
        q,
        sort,
        agentId
      );
      let filterActive = false;
      if (statusValue || q || agentId) {
        filterActive = true;
      }
      return {
        jobs: jobs.data,
        total: jobs.total,
        version,
        filterActive,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchJobsListWithBerthDataStatic = createAsyncThunk(
  "voyages/fetchJobsListWithBerthDataStatic",
  async (
    {
      page,
      pageSize,
    }: {
      page: number;
      pageSize: number | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const jobs = await getJobsListWithBerthData(page, pageSize, "active");
      return {
        jobs: jobs.data,
        total: jobs.total,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
